import React from 'react';
import VraagTekst from './VraagTekst';
import ZoomAfbeelding from './ZoomAfbeelding';
import './InhoudBlokken.css';

/**
 * InhoudBlokken — rijke vraaginhoud: tekst, kader, lijst, formule, tabel, figuur.
 *
 * Komt uit de CE-vragenbank (vragen.inhoud en vragen.bron_inhoud, migratie 077;
 * formaat in content/ce/FORMAT.md §5). Tekst is een kleine markdown-subset:
 * **vet**, *cursief* en LaTeX tussen $…$ (via VraagTekst/KaTeX). Geen HTML:
 * alles blijft React-tekst, alleen KaTeX-uitvoer gaat via VraagTekst.
 *
 * Blokken: tekst (evt. alinea-nummer of stijl 'voetnoot' / 'bronvermelding'), kop, kader, lijst,
 * formule, tabel (cellen als string of {md, colspan, rowspan, kop}; kop_kolom
 * maakt van de eerste kolom rijkoppen), figuur (titel/bijschrift staan niet in
 * de uitsnede en worden hier getoond; klikken vergroot de figuur in een venster
 * binnen de pagina, zie ZoomAfbeelding).
 *
 * Ook gebruikt voor eigen bronnen bij Mens & maatschappij (utils/bronvraag.js).
 *
 * Props:
 *   blokken   array van blokken (null/[] → niets)
 *   compact   kleinere figuren (lijstweergave in de bank)
 */

// Geen regex-lookbehind: Safari/iPadOS < 16.4 laadt de bundel daar niet. De
// inhoud eindigt op een niet-spatie (laatste token \x of één teken); zelfde
// gedrag als de oude versie met lookbehind, zie utils/geenLookbehind.test.js.
export const FORMULE = /\$\$[\s\S]+?\$\$|\$(?=\S)(?:\\.|[^$\\])*?(?:\\\S|[^$\\\s])\$/g;
export const NADRUK = /\*\*(.+?)\*\*|\*(?=\S)(.*?\S)\*/g;

function nadruk(tekst, sleutel) {
  const uit = [];
  let laatste = 0;
  let m;
  NADRUK.lastIndex = 0;
  while ((m = NADRUK.exec(tekst)) !== null) {
    if (m.index > laatste) uit.push(tekst.slice(laatste, m.index));
    uit.push(m[1] !== undefined
      ? <strong key={`${sleutel}-${m.index}`}>{m[1]}</strong>
      : <em key={`${sleutel}-${m.index}`}>{m[2]}</em>);
    laatste = m.index + m[0].length;
  }
  if (laatste < tekst.length) uit.push(tekst.slice(laatste));
  return uit;
}

/** Eén regel markdown-subset: formules apart, daarbuiten vet/cursief. */
export function MdTekst({ md, as: Tag = 'span', className }) {
  const bron = String(md ?? '');
  const delen = [];
  let laatste = 0;
  let m;
  FORMULE.lastIndex = 0;
  while ((m = FORMULE.exec(bron)) !== null) {
    if (m.index > 0 && bron[m.index - 1] === '\\') continue;
    if (m.index > laatste) delen.push(...nadruk(bron.slice(laatste, m.index).replace(/\\\$/g, '$'), `t${m.index}`));
    delen.push(<VraagTekst key={`f${m.index}`} tekst={m[0]} />);
    laatste = m.index + m[0].length;
  }
  if (laatste < bron.length) delen.push(...nadruk(bron.slice(laatste).replace(/\\\$/g, '$'), 'eind'));
  return <Tag className={className}>{delen}</Tag>;
}

function Figuur({ blok, compact }) {
  // Meetfiguren op ware grootte (breedte op papier), anders schalen tot de kolom.
  const stijl = blok.meetfiguur && blok.breedte_cm ? { width: `${blok.breedte_cm}cm`, maxWidth: 'none' } : undefined;
  return (
    <figure className={`ib-figuur${compact ? ' ib-compact' : ''}${blok.meetfiguur ? ' ib-meet' : ''}`}>
      {blok.titel && <figcaption className="ib-fig-titel"><MdTekst md={blok.titel} /></figcaption>}
      <ZoomAfbeelding src={blok.url} alt={blok.alt || blok.bijschrift || blok.titel || 'figuur'} loading="lazy" style={stijl} />
      {blok.bijschrift && <figcaption className="ib-fig-bijschrift"><MdTekst md={blok.bijschrift} /></figcaption>}
      {blok.herkomst && <div className="ib-herkomst">{blok.herkomst}</div>}
    </figure>
  );
}

/** Tabelcel: string of {md, colspan?, rowspan?, kop?}. */
function Cel({ cel, kopRij, rijKop }) {
  const obj = cel && typeof cel === 'object' ? cel : { md: cel ?? '' };
  const isKop = kopRij || rijKop || obj.kop === true;
  const Tag = isKop ? 'th' : 'td';
  const scope = kopRij ? 'col' : isKop ? 'row' : undefined;
  return (
    <Tag scope={scope} colSpan={obj.colspan > 1 ? obj.colspan : undefined} rowSpan={obj.rowspan > 1 ? obj.rowspan : undefined}>
      <MdTekst md={obj.md} />
    </Tag>
  );
}

function Tabel({ blok }) {
  return (
    <div className="ib-tabel-wrap">
      {blok.titel && <div className="ib-tabel-titel"><MdTekst md={blok.titel} /></div>}
      <table className="ib-tabel">
        {blok.kop?.length > 0 && (
          <thead>
            {blok.kop.map((rij, r) => (
              <tr key={r}>{rij.map((cel, c) => <Cel key={c} cel={cel} kopRij />)}</tr>
            ))}
          </thead>
        )}
        <tbody>
          {(blok.rijen || []).map((rij, r) => (
            <tr key={r}>{rij.map((cel, c) => <Cel key={c} cel={cel} rijKop={blok.kop_kolom && c === 0} />)}</tr>
          ))}
        </tbody>
      </table>
      {blok.bijschrift && <div className="ib-fig-bijschrift"><MdTekst md={blok.bijschrift} /></div>}
      {blok.herkomst && <div className="ib-herkomst">{blok.herkomst}</div>}
    </div>
  );
}

function Blok({ blok, compact }) {
  switch (blok?.type) {
    case 'tekst': {
      if (blok.stijl === 'bronvermelding') return <MdTekst as="p" className="ib-herkomst ib-bronvermelding" md={`Bron: ${blok.md ?? ''}`} />;
      const klasse = blok.stijl === 'voetnoot' ? 'ib-tekst ib-voetnoot' : 'ib-tekst';
      // Het alineanummer staat in `alinea`; de regex is een vangnet voor oude invoer met '**3** ' in md.
      return blok.alinea
        ? <p className={`ib-alinea${blok.stijl === 'voetnoot' ? ' ib-voetnoot' : ''}`}><span className="ib-alinea-nr">{blok.alinea}</span><MdTekst md={String(blok.md ?? '').replace(/^\*\*\d+\*\*\s*/, '')} /></p>
        : <MdTekst as="p" className={klasse} md={blok.md} />;
    }
    case 'kop':
      return <MdTekst as="p" className="ib-kop" md={blok.md} />;
    case 'kader':
      return (
        <div className="ib-kader">
          {blok.titel && <div className="ib-kader-titel"><MdTekst md={blok.titel} /></div>}
          <InhoudBlokken blokken={blok.inhoud} compact={compact} />
        </div>
      );
    case 'lijst': {
      const Tag = blok.genummerd ? 'ol' : 'ul';
      return (
        <Tag className="ib-lijst" start={blok.genummerd ? blok.start || 1 : undefined}>
          {(blok.items || []).map((it, i) => <li key={i}><MdTekst md={it} /></li>)}
        </Tag>
      );
    }
    case 'formule':
      return <div className="ib-formule"><VraagTekst tekst={`$$${blok.latex}$$`} /></div>;
    case 'tabel':
      return <Tabel blok={blok} />;
    case 'figuur':
      return <Figuur blok={blok} compact={compact} />;
    default:
      return null;
  }
}

export default function InhoudBlokken({ blokken, compact = false, className }) {
  if (!Array.isArray(blokken) || blokken.length === 0) return null;
  return (
    <div className={`ib-inhoud${className ? ` ${className}` : ''}`}>
      {blokken.map((b, i) => <Blok key={b.id || i} blok={b} compact={compact} />)}
    </div>
  );
}
